// vistas/PedidoDetalleProv.jsx
import React, { useState, useMemo, useEffect } from 'react';
import { Search, Loader2, AlertCircle, Package, Users, ChevronDown, ChevronRight, Plus, Percent, Trash2, Minus, Tags, Building2, CheckCircle, Eye, EyeOff } from 'lucide-react';
import MultiSelectFilter from './MultiSelectFilter';
import VistaResumen from './PedidosResumen';
import { useQuery } from '../QueryContext';

const formatUSD = (num) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(num || 0);

// ================================================================
// MAPEO DE PROVEEDORES - Agrupa nombres equivalentes
// ================================================================
const MAPEO_PROVEEDORES = {
  'BEYAZ KAGIT VE HIJYENIK ÜRÜNLER TEM. INS SAN. TIC A.S.': 'BEYAZ KAGIT',
  'BEYAZ KAGIT': 'BEYAZ KAGIT',
  'Canalcare, S.A.': 'Canalcare, S.A.',
  'CANALCARE': 'Canalcare, S.A.',
  'COLOMBIANA KIMBERLY COLPAPEL S.A.': 'KIMBERLY-CLARK',
  'KIMBERLY-CLARK PERÚ S.R.L.': 'KIMBERLY-CLARK',
  'KIMBERLY-CLARK': 'KIMBERLY-CLARK',
  'KIMBERLY CLARK': 'KIMBERLY-CLARK',
  'EDGEWELL PERSONAL CARE, LLC': 'EDGEWELL',
  'EDGEWELL': 'EDGEWELL',
  'ENERGIZER, LLC': 'ENERGIZER, LLC',
  'ENERGIZER': 'ENERGIZER, LLC',
  'PURISSIMA DE VENEZUELA I C.A.': 'PURISSIMA',
  'PURISSIMA': 'PURISSIMA',
  'UNILEVER ANDINA VENEZUELA, S.A.': 'UNILEVER INTERNATIONAL',
  'UNILEVER ASIA PRIVATE LIMITED': 'UNILEVER INTERNATIONAL',
  'UNILEVER': 'UNILEVER INTERNATIONAL',
  'UNILEVER INTERNATIONAL': 'UNILEVER INTERNATIONAL',
  'VENTEKS DIS TICARET LIMITED SIRKETI': 'VENTEKS',
  'VENTEKS': 'VENTEKS',
  '': 'SIN PROVEEDOR',
};

/**
 * Normaliza el nombre del proveedor.
 * Si está en el mapeo, devuelve el nombre nuevo.
 * Si NO está, devuelve el nombre original tal como viene.
 */
const normalizarProveedor = (nombreOriginal) => {
  if (!nombreOriginal || nombreOriginal.trim() === '') {
    return 'SIN PROVEEDOR';
  }
  
  const nombreLimpio = nombreOriginal.trim().toUpperCase();
  
  // Buscar coincidencia exacta primero
  if (MAPEO_PROVEEDORES[nombreLimpio]) {
    return MAPEO_PROVEEDORES[nombreLimpio];
  }
  
  // Buscar coincidencia parcial (por si hay variaciones)
  for (const [clave, valor] of Object.entries(MAPEO_PROVEEDORES)) {
    if (clave && (nombreLimpio.includes(clave) || clave.includes(nombreLimpio))) {
      return valor;
    }
  }
  
  // Si no está en el mapeo, mantener el nombre original
  return nombreOriginal.trim();
};

// ================================================================
// FUNCIÓN AUXILIAR PARA OBTENER PRECIOS DE DIFERENTES FORMATOS
// ================================================================
const getPrecioProducto = (product, field = 'usd') => {
  if (!product) return 0;
  
  const posiblesCampos = [
    field === 'usd' ? 'precio_usd' : 'precio_bs',
    field === 'usd' ? 'precioUsd' : 'precioBs',
    field === 'usd' ? 'precio_dolar' : 'precio_bolivar',
    field === 'usd' ? 'price_usd' : 'price_bs',
    field === 'usd' ? 'priceUSD' : 'priceBS',
    'precio',
    'price',
  ];
  
  for (const campo of posiblesCampos) {
    if (product[campo] !== undefined && product[campo] !== null) {
      const val = parseFloat(product[campo]);
      if (!isNaN(val) && val > 0) return val;
    }
  }
  
  if (product.precios && typeof product.precios === 'object') {
    const val = field === 'usd' ? product.precios.usd || product.precios.USD : product.precios.bs || product.precios.BS;
    if (val !== undefined && val !== null) {
      const parsed = parseFloat(val);
      if (!isNaN(parsed) && parsed > 0) return parsed;
    }
  }
  
  return 0;
};

// ================================================================
// COMPONENTE PRINCIPAL
// ================================================================
const VistaDetalleProveedor = ({ hook }) => {
  const { getProductosCliente } = useQuery();

  const {
    setSelectedClients,
    orderLines,
    handleAddProduct,
    clientesOptions,
    handleUpdateClientQuantity,
    handleUpdateClientDiscount,
    handleUpdateClientPacDiscount,
    handleRemoveProduct,
    successModal,
    closeSuccessModal,
  } = hook;

  // Estado local: 1 solo cliente
  const [clienteSeleccionado, setClienteSeleccionado] = useState(null);
  const [clientSearchTerm, setClientSearchTerm] = useState('');

  // Filtros múltiples
  const [selectedProveedores, setSelectedProveedores] = useState([]);
  const [selectedMarcas, setSelectedMarcas] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');

  // ✅ Toggle para mostrar/ocultar productos sin precio
  const [mostrarSinPrecio, setMostrarSinPrecio] = useState(false);

  // Estado LOCAL para cantidades y descuentos ANTES de agregar
  const [localAssignments, setLocalAssignments] = useState({});

  // Acordeón
  const [expandedProveedores, setExpandedProveedores] = useState({});
  const [expandedMarcas, setExpandedMarcas] = useState({});

  // Productos del cliente único
  const [allProductosCliente, setAllProductosCliente] = useState([]);
  const [loadingProductos, setLoadingProductos] = useState(false);

  // Cargar productos cuando cambia el cliente seleccionado
  useEffect(() => {
    if (!clienteSeleccionado) {
      setAllProductosCliente([]);
      setLocalAssignments({});
      return;
    }
    const fetchProductos = async () => {
      setLoadingProductos(true);
      try {
        const data = await getProductosCliente(clienteSeleccionado);
        if (data?.success) {
          setAllProductosCliente(data.productos);
        }
      } catch (err) {
        console.error('Error cargando productos:', err);
        setAllProductosCliente([]);
      } finally {
        setLoadingProductos(false);
      }
    };
    fetchProductos();
  }, [clienteSeleccionado, getProductosCliente]);

  const handleSelectCliente = (clientId) => {
    if (clienteSeleccionado === clientId) {
      setClienteSeleccionado(null);
      setSelectedClients([]);
      setLocalAssignments({});
    } else {
      setClienteSeleccionado(clientId);
      setSelectedClients([clientId]);
    }
  };

  const filteredClients = useMemo(() => {
    if (!clientSearchTerm) return clientesOptions;
    const term = clientSearchTerm.toLowerCase();
    return clientesOptions.filter(c =>
      c.value.toLowerCase().includes(term) ||
      c.label.toLowerCase().includes(term) ||
      (c.nombre && c.nombre.toLowerCase().includes(term))
    );
  }, [clientesOptions, clientSearchTerm]);

  // ✅ Productos filtrados con mapeo de proveedores + filtro sin precio
  const productosFiltrados = useMemo(() => {
    let prods = allProductosCliente;
    if (selectedProveedores.length > 0) {
      prods = prods.filter(p => {
        const prov = normalizarProveedor(p.proveedor || p.codigo_vendedor || 'SIN PROVEEDOR');
        return selectedProveedores.includes(prov);
      });
    }
    if (selectedMarcas.length > 0) {
      prods = prods.filter(p => selectedMarcas.includes(p.marca));
    }
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      prods = prods.filter(p =>
        (p.sku && p.sku.toLowerCase().includes(term)) ||
        (p.nombre && p.nombre.toLowerCase().includes(term))
      );
    }
    // ✅ Filtro de sin precio
    if (!mostrarSinPrecio) {
      prods = prods.filter(p => {
        const precioUSD = getPrecioProducto(p, 'usd');
        const precioBS = getPrecioProducto(p, 'bs');
        return !(precioUSD <= 0 && precioBS <= 0);
      });
    }
    return prods;
  }, [allProductosCliente, selectedProveedores, selectedMarcas, searchTerm, mostrarSinPrecio]);

  // Agrupar por proveedor normalizado > Marca
  const productosAgrupados = useMemo(() => {
    const agrupado = {};
    productosFiltrados.forEach(p => {
      const prov = normalizarProveedor(p.proveedor || p.codigo_vendedor || 'SIN PROVEEDOR');
      const marca = p.marca || 'Sin Marca';
      if (!agrupado[prov]) agrupado[prov] = {};
      if (!agrupado[prov][marca]) agrupado[prov][marca] = [];
      agrupado[prov][marca].push(p);
    });
    return agrupado;
  }, [productosFiltrados]);

  // Opciones de proveedores normalizados
  const proveedoresOptions = useMemo(() => {
    const uniqueProvs = new Set();
    allProductosCliente.forEach(p => {
      const provAgrupado = normalizarProveedor(p.proveedor || p.codigo_vendedor || 'SIN PROVEEDOR');
      uniqueProvs.add(provAgrupado);
    });
    return [...uniqueProvs].sort();
  }, [allProductosCliente]);

  // Opciones de marcas filtradas por proveedor normalizado
  const marcasOptions = useMemo(() => {
    const uniqueMarcas = new Set();
    allProductosCliente.forEach(p => {
      const provAgrupado = normalizarProveedor(p.proveedor || p.codigo_vendedor || 'SIN PROVEEDOR');
      if (selectedProveedores.length === 0 || selectedProveedores.includes(provAgrupado)) {
        uniqueMarcas.add(p.marca);
      }
    });
    return [...uniqueMarcas].sort();
  }, [allProductosCliente, selectedProveedores]);

  const isInOrder = (sku) => orderLines.some(l => l.sku === sku);
  

  const toggleProveedor = (prov) => {
    setExpandedProveedores(prev => ({ ...prev, [prov]: !prev[prov] }));
  };

  const toggleMarca = (prov, marca) => {
    const key = `${prov}|||${marca}`;
    setExpandedMarcas(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const expandAll = () => {
    const newExpProv = {};
    const newExpMarc = {};
    Object.keys(productosAgrupados).forEach(prov => {
      newExpProv[prov] = true;
      Object.keys(productosAgrupados[prov]).forEach(marca => {
        newExpMarc[`${prov}|||${marca}`] = true;
      });
    });
    setExpandedProveedores(newExpProv);
    setExpandedMarcas(newExpMarc);
  };

  const collapseAll = () => {
    setExpandedProveedores({});
    setExpandedMarcas({});
  };

  // Obtener o crear asignación LOCAL para un SKU
  const getLocalAssignment = (sku) => {
    return localAssignments[sku] || { cantidad: 0, descuento: 0, descuento_pac: 0, descuento_final: 0 };
  };

  // Actualizar asignación LOCAL
  const updateLocalAssignment = (sku, updates) => {
    setLocalAssignments(prev => ({
      ...prev,
      [sku]: { ...getLocalAssignment(sku), ...updates }
    }));
  };

  // Agregar producto con sus valores locales
  const handleAddProductLocal = (product) => {
    const local = getLocalAssignment(product.sku);
    const precioUSD = getPrecioProducto(product, 'usd');
    const precioBS = getPrecioProducto(product, 'bs');
    
    const productFormatted = {
      sku: product.sku,
      marca: product.marca,
      nombre: product.nombre,
      uni_venta: product.uni_venta || product.unidad_venta || 'UND',
      tipo_imp: product.tipo_imp || product.tipoImp || 1,
      equivalencia: product.equivalencia || 1.0,
      stock_bl: product.stock_bl !== undefined ? product.stock_bl : product.stock,
      clientPrecios: {
        [clienteSeleccionado]: {
          precio_bs: precioBS,
          precio_bs2: parseFloat(product.precio_bs2 || product.precioBs2 || product.precio_bs_2 || 0) || 0,
          precio_usd: precioUSD,
          codigo_vendedor: product.codigo_vendedor || product.codigoVendedor || '01',
          cos_pro_un: parseFloat(product.cos_pro_un || product.cosProUn || 0) || 0,
          ult_cos_un: parseFloat(product.ult_cos_un || product.ultCosUn || 0) || 0,
          cos_pro_om: parseFloat(product.cos_pro_om || product.cosProOm || 0) || 0,
          ult_cos_om: parseFloat(product.ult_cos_om || product.ultCosOm || 0) || 0
        }
      }
    };
    
    // Primero agregar al hook
    handleAddProduct(productFormatted);
    
    // Luego aplicar los valores locales
    if (local.cantidad > 0) {
      handleUpdateClientQuantity(product.sku, clienteSeleccionado, local.cantidad);
    }
    if (local.descuento > 0) {
      handleUpdateClientDiscount(product.sku, clienteSeleccionado, local.descuento);
    }
    if (local.descuento_pac > 0) {
      handleUpdateClientPacDiscount(product.sku, clienteSeleccionado, local.descuento_pac);
    }
  };

  // Actualizar cantidad local
  const updateLocalCantidad = (sku, valor) => {
    const cant = Math.max(0, parseInt(valor) || 0);
    updateLocalAssignment(sku, { cantidad: cant });
    
    if (isInOrder(sku)) {
      handleUpdateClientQuantity(sku, clienteSeleccionado, cant);
    }
  };

  // Actualizar descuento propuesta local
  const updateLocalDescuento = (sku, valor) => {
    const desc = Math.min(100, Math.max(0, parseFloat(valor) || 0));
    updateLocalAssignment(sku, { descuento: desc });
    
    if (isInOrder(sku)) {
      handleUpdateClientDiscount(sku, clienteSeleccionado, desc);
    }
  };

  // Actualizar descuento PAC local
  const updateLocalDescuentoPAC = (sku, valor) => {
    const descPAC = Math.min(100, Math.max(0, parseFloat(valor) || 0));
    updateLocalAssignment(sku, { descuento_pac: descPAC });
    
    if (isInOrder(sku)) {
      handleUpdateClientPacDiscount(sku, clienteSeleccionado, descPAC);
    }
  };

  return (
    <div className="mass-order-body">
      {/* COLUMNA 1 - CLIENTE ÚNICO */}
      <div className="mass-col mass-col-clients">
        <div className="col-header">
          <Users size={18} />
          <h3>Cliente</h3>
          {clienteSeleccionado && <span className="badge">1</span>}
        </div>
        <div className="col-search">
          <Search size={14} />
          <input
            type="text"
            placeholder="Buscar cliente..."
            value={clientSearchTerm}
            onChange={(e) => setClientSearchTerm(e.target.value)}
          />
        </div>
        <div className="client-list">
          {filteredClients.map(client => (
            <div
              key={client.value}
              className={`client-item ${clienteSeleccionado === client.value ? 'selected' : ''}`}
              onClick={() => handleSelectCliente(client.value)}
            >
              <div className="client-info">
                <div className="client-code">{client.value}</div>
                <div className="client-name">{client.nombre}</div>
                {client.porc_desc > 0 && (
                  <span style={{ fontSize: '0.6rem', color: '#f87171' }}>
                    Desc. {client.porc_desc}%
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* COLUMNA 2 - TABLA DE PRODUCTOS CON ACORDEÓN */}
      <div className="mass-col mass-col-products">
        <div className="col-header">
          <Package size={18} />
          <h3>Productos por Proveedor</h3>
          <span className="badge">{productosFiltrados.length}</span>
        </div>

        {/* Filtros */}
        <div className="product-filters" style={{ display: 'flex', flexDirection: 'row', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
          <MultiSelectFilter
            label="Proveedor"
            options={proveedoresOptions.map(p => ({ value: p, label: p }))}
            selected={selectedProveedores}
            onChange={setSelectedProveedores}
          />
          <MultiSelectFilter
            label="Marca"
            options={marcasOptions.map(m => ({ value: m, label: m }))}
            selected={selectedMarcas}
            onChange={setSelectedMarcas}
          />
          <div className="col-search" style={{ flex: 1, minWidth: '200px' }}>
            <Search size={14} />
            <input
              type="text"
              placeholder="Buscar SKU o nombre..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        {/* ✅ Botones Expandir/Colapsar + Toggle Sin Precio */}
        <div style={{ display: 'flex', gap: '0.5rem', padding: '0.3rem 0.8rem', alignItems: 'center', flexWrap: 'wrap' }}>
          <button onClick={expandAll} className="new-order-btn" style={{ padding: '4px 10px', fontSize: '0.6rem', minWidth: 'auto', background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)' }}>
            <ChevronDown size={12} /> Expandir
          </button>
          <button onClick={collapseAll} className="new-order-btn" style={{ padding: '4px 10px', fontSize: '0.6rem', minWidth: 'auto', background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)' }}>
            <ChevronRight size={12} /> Colapsar
          </button>
          
          {/* ✅ TOGGLE SIN PRECIO */}
          <button
            onClick={() => setMostrarSinPrecio(prev => !prev)}
            className="new-order-btn"
            style={{
              padding: '4px 10px',
              fontSize: '0.6rem',
              minWidth: 'auto',
              background: mostrarSinPrecio ? 'rgba(239,68,68,0.15)' : 'rgba(0,186,82,0.15)',
              border: mostrarSinPrecio ? '1px solid rgba(239,68,68,0.4)' : '1px solid rgba(0,186,82,0.4)',
              color: mostrarSinPrecio ? '#f87171' : '#4ade80',
              display: 'flex',
              alignItems: 'center',
              gap: '4px'
            }}
            title={mostrarSinPrecio ? 'Ocultar productos sin precio' : 'Mostrar productos sin precio'}
          >
            {mostrarSinPrecio ? <Eye size={12} /> : <EyeOff size={12} />}
            {mostrarSinPrecio ? 'Ocultando Sin Precio': 'Mostrar Sin Precio' }
          </button>
        </div>

        {/* Contenido con acordeón */}
        <div style={{ flex: 1, overflow: 'auto', padding: '0.5rem' }}>
          {loadingProductos ? (
            <div className="loading-state"><Loader2 size={30} className="spin-anim" /><p>Cargando...</p></div>
          ) : !clienteSeleccionado ? (
            <div className="empty-state"><AlertCircle size={40} /><p>Seleccione un cliente</p></div>
          ) : Object.keys(productosAgrupados).length === 0 ? (
            <div className="empty-state"><Building2 size={40} /><p>No se encontraron productos</p></div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
              {Object.entries(productosAgrupados).map(([prov, marcas]) => {
                const isProvExpanded = expandedProveedores[prov] || false;
                const totalProductosProveedor = Object.values(marcas).flat().length;
                
                return (
                  <div key={prov} style={{ border: '1px solid rgba(255,255,255,0.06)', borderRadius: '8px', overflow: 'hidden' }}>
                    {/* HEADER PROVEEDOR */}
                    <div
                      onClick={() => toggleProveedor(prov)}
                      style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 12px', background: 'rgba(255,255,255,0.05)', cursor: 'pointer', transition: 'background 0.15s', userSelect: 'none' }}
                      onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.08)'}
                      onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.05)'}
                    >
                      {isProvExpanded ? <ChevronDown size={16} style={{ color: '#00ba52' }} /> : <ChevronRight size={16} style={{ color: '#888' }} />}
                      <Building2 size={14} style={{ color: '#00ba52' }} />
                      <span style={{ fontWeight: 700, color: '#fff', fontSize: '0.8rem' }}>{prov}</span>
                      <span style={{ fontSize: '0.65rem', color: '#888', marginLeft: 'auto' }}>
                        {totalProductosProveedor} producto(s) | {Object.keys(marcas).length} marca(s)
                      </span>
                    </div>

                    {/* MARCAS */}
                    {isProvExpanded && (
                      <div style={{ paddingLeft: '20px' }}>
                        {Object.entries(marcas).map(([marca, prods]) => {
                          const keyMarca = `${prov}|||${marca}`;
                          const isMarcaExpanded = expandedMarcas[keyMarca] || false;
                          
                          return (
                            <div key={marca} style={{ borderTop: '1px solid rgba(255,255,255,0.03)' }}>
                              {/* HEADER MARCA */}
                              <div
                                onClick={(e) => { e.stopPropagation(); toggleMarca(prov, marca); }}
                                style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 12px', cursor: 'pointer', transition: 'background 0.15s', userSelect: 'none' }}
                                onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.03)'}
                                onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                              >
                                {isMarcaExpanded ? <ChevronDown size={14} style={{ color: '#a78bfa' }} /> : <ChevronRight size={14} style={{ color: '#666' }} />}
                                <Tags size={12} style={{ color: '#a78bfa' }} />
                                <span style={{ fontWeight: 600, color: '#aaa', fontSize: '0.7rem' }}>{marca}</span>
                                <span style={{ fontSize: '0.6rem', color: '#666', marginLeft: 'auto' }}>{prods.length} producto(s)</span>
                              </div>

                              {/* PRODUCTOS */}
                              {isMarcaExpanded && (
                                <div style={{ paddingLeft: '20px', paddingBottom: '4px' }}>
                                  {prods.map((p, index) => {
                                    const added = isInOrder(p.sku);
                                    const sinStock = p.stock_bl !== null && p.stock_bl <= 0;
                                    
                                    const precioUSD = getPrecioProducto(p, 'usd');
                                    const precioBS = getPrecioProducto(p, 'bs');
                                    const sinPrecio = precioUSD <= 0 && precioBS <= 0;
                                    
                                    const localAssign = getLocalAssignment(p.sku);
                                    const cantidad = localAssign.cantidad;
                                    const descPropuesta = localAssign.descuento;
                                    const descPAC = localAssign.descuento_pac;
                                    const subtotal = cantidad * precioUSD;

                                    return (
                                      <div
                                        key={`${p.sku}-${index}`}
                                        style={{
                                          display: 'flex', alignItems: 'center', gap: '6px',
                                          padding: '6px 8px',
                                          borderBottom: '1px solid rgba(255,255,255,0.02)',
                                          opacity: sinPrecio ? 0.5 : 1,
                                          background: added ? 'rgba(0,186,82,0.05)' : 'transparent',
                                          flexWrap: 'wrap'
                                        }}
                                      >
                                        {/* SKU */}
                                        <span style={{ fontFamily: 'monospace', color: '#00ba52', fontSize: '0.7rem', fontWeight: 700, minWidth: '110px' }}>
                                          {p.sku}
                                        </span>

                                        {/* Nombre */}
                                        <span style={{ fontSize: '0.68rem', color: '#ccc', flex: 1, minWidth: '100px' }}>
                                          {p.nombre}
                                        </span>

                                        {/* Stock - informativo */}
                                        <span style={{ fontSize: '0.85rem', color: sinStock ? '#f87171' : '#2dd4bf', minWidth: '50px', textAlign: 'center' }}>
                                          {p.stock_bl !== null ? `Stock: ${p.stock_bl}` : 'N/D'}
                                        </span>

                                        {/* Precio USD */}
                                        <span style={{ fontSize: '0.7rem', fontWeight: 700, color: sinPrecio ? '#f87171' : '#fff', minWidth: '70px', textAlign: 'right' }}>
                                          {sinPrecio ? 'Sin precio' : formatUSD(precioUSD)}
                                        </span>

                                        {/* Cantidad - SIEMPRE VISIBLE */}
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '3px', background: 'rgba(255,255,255,0.05)', borderRadius: '4px', padding: '2px 4px' }}>
                                          <button
                                            onClick={() => updateLocalCantidad(p.sku, cantidad - 1)}
                                            disabled={cantidad <= 0 || sinPrecio}
                                            style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer', padding: '0 2px', opacity: cantidad <= 0 ? 0.3 : 1 }}
                                          >
                                            <Minus size={10} />
                                          </button>
                                          <input
                                            type="number"
                                            min="0"
                                            value={cantidad}
                                            onChange={(e) => updateLocalCantidad(p.sku, e.target.value)}
                                            disabled={sinPrecio}
                                            style={{ width: '35px', background: 'transparent', border: 'none', color: '#fff', fontSize: '0.65rem', textAlign: 'center', outline: 'none' }}
                                          />
                                          <button
                                            onClick={() => updateLocalCantidad(p.sku, cantidad + 1)}
                                            disabled={sinPrecio}
                                            style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer', padding: '0 2px', opacity: sinPrecio ? 0.3 : 1 }}
                                          >
                                            <Plus size={10} />
                                          </button>
                                        </div>

                                        {/* Desc. Propuesta - SIEMPRE VISIBLE */}
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '2px', background: 'rgba(167,139,250,0.08)', borderRadius: '4px', padding: '2px 4px' }}>
                                          <Percent size={9} style={{ color: '#a78bfa' }} />
                                          <span style={{ fontSize: '0.75rem', color: '#a78bfa', minWidth: '50px', textAlign: 'center' }}>
                                          { `Desc:` }
                                        </span>
                                          <input
                                            type="number"
                                            min="0"
                                            max="100"
                                            step="1"
                                            value={descPropuesta}
                                            onChange={(e) => updateLocalDescuento(p.sku, e.target.value)}
                                            disabled={sinPrecio}
                                            style={{ width: '30px', background: 'transparent', border: 'none', color: '#fff', fontSize: '0.6rem', textAlign: 'center', outline: 'none' }}
                                          />
                                          <span style={{ fontSize: '0.5rem', color: '#a78bfa' }}>%</span>
                                        </div>

                                        {/* Desc. PAC - SIEMPRE VISIBLE */}
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '2px', background: 'rgba(34,197,94,0.08)', borderRadius: '4px', padding: '2px 4px' }}>
                                          <Percent size={9} style={{ color: '#4ade80' }} />
                                          <span style={{ fontSize: '0.75rem', color: '#4ade80', minWidth: '50px', textAlign: 'center' }}>
                                          { `PAC:` }
                                        </span>
                                          <input
                                            type="number"
                                            min="0"
                                            max="100"
                                            step="1"
                                            value={descPAC}
                                            onChange={(e) => updateLocalDescuentoPAC(p.sku, e.target.value)}
                                            disabled={sinPrecio}
                                            style={{ width: '30px', background: 'transparent', border: 'none', color: '#fff', fontSize: '0.6rem', textAlign: 'center', outline: 'none' }}
                                          />
                                          <span style={{ fontSize: '0.5rem', color: '#4ade80' }}>%</span>
                                        </div>

                                        {/* Subtotal - SIEMPRE VISIBLE */}
                                        <span style={{ fontSize: '0.7rem', fontWeight: 700, color: '#fff', minWidth: '65px', textAlign: 'right' }}>
                                          {formatUSD(subtotal)}
                                        </span>

                                        {/* Botón Agregar/Quitar */}
                                        {!added ? (
                                          <button
                                            onClick={() => handleAddProductLocal(p)}
                                            disabled={sinPrecio || cantidad <= 0}
                                            className="new-order-btn"
                                            style={{
                                              padding: '4px 8px', fontSize: '0.6rem', minWidth: 'auto',
                                              opacity: (sinPrecio || cantidad <= 0) ? 0.4 : 1,
                                              background: (sinPrecio || cantidad <= 0) ? 'rgba(255,255,255,0.08)' : 'var(--text-theme, #00ba52)',
                                              border: 'none', borderRadius: '6px',
                                              display: 'flex', alignItems: 'center', gap: '4px',
                                              cursor: (sinPrecio || cantidad <= 0) ? 'not-allowed' : 'pointer'
                                            }}
                                          >
                                            <Plus size={12} /> Agregar
                                          </button>
                                        ) : (
                                          <button
                                            onClick={() => handleRemoveProduct(p.sku)}
                                            style={{ background: 'none', border: 'none', color: '#f87171', cursor: 'pointer', padding: '2px' }}
                                          >
                                            <Trash2 size={14} />
                                          </button>
                                        )}
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* COLUMNA 3 - RESUMEN */}
      <VistaResumen hook={hook} />

      {successModal.show && (
  <>
    <div className="order-panel-overlay" style={{ zIndex: 1001 }} onClick={closeSuccessModal} />
    <div style={{ 
      position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', 
      background: 'rgba(15, 20, 35, 0.98)', backdropFilter: 'blur(20px)', 
      borderRadius: '20px', padding: '2rem', textAlign: 'center', zIndex: 1002, 
      boxShadow: '0 10px 30px rgba(0,0,0,0.6)', border: '1px solid rgba(255,255,255,0.08)', 
      width: '90%', maxWidth: '550px', maxHeight: '80vh', overflow: 'auto', 
      fontFamily: 'Montserrat, sans-serif' 
    }}>
      <CheckCircle size={48} style={{ color: '#00ba52', marginBottom: '1rem' }} />
      <p style={{ color: '#fff', fontSize: '1rem', fontWeight: 600, margin: '0.5rem 0' }}>
        {successModal.message}
      </p>

      {/* ✅ SKUs no procesados */}
      {successModal.skus_no_procesados && successModal.skus_no_procesados.length > 0 && (
        <div style={{ 
          marginTop: '1rem', textAlign: 'left', 
          background: 'rgba(239,68,68,0.1)', 
          border: '1px solid rgba(239,68,68,0.3)', 
          borderRadius: '8px', padding: '0.8rem' 
        }}>
          <h4 style={{ color: '#f87171', fontSize: '0.8rem', marginBottom: '0.5rem', fontWeight: 600 }}>
            ⚠️ SKUs no procesados ({successModal.skus_no_procesados.length}):
          </h4>
          <div style={{ maxHeight: '120px', overflowY: 'auto' }}>
            {successModal.skus_no_procesados.map((sku, idx) => (
              <div key={sku} style={{ 
                color: '#f87171', fontSize: '0.7rem', fontFamily: 'monospace', 
                padding: '2px 0', borderBottom: '1px solid rgba(239,68,68,0.1)' 
              }}>
                • {sku}
              </div>
            ))}
          </div>
          <p style={{ color: '#f87171', fontSize: '0.6rem', marginTop: '0.5rem', fontStyle: 'italic' }}>
            Estos SKUs no existen en la tabla de artículos de SQL Server.
          </p>
        </div>
      )}

      {/* ✅ Pedidos creados */}
      {successModal.pedidos && successModal.pedidos.length > 0 && (
        <div style={{ marginTop: '1rem', textAlign: 'left' }}>
          {successModal.pedidos.map((pedido, idx) => (
            <div key={idx} style={{ 
              background: 'rgba(255,255,255,0.05)', borderRadius: '8px', 
              padding: '0.5rem', marginBottom: '0.5rem', 
              display: 'flex', justifyContent: 'space-between', alignItems: 'center' 
            }}>
              <div>
                <div style={{ color: '#00ba52', fontWeight: 600 }}>
                  Pedido #{pedido.factNum}
                  {pedido.lote && <span style={{ fontSize: '0.7rem', color: '#888' }}> (Lote {pedido.lote})</span>}
                </div>
                <div style={{ color: '#aaa', fontSize: '0.8rem' }}>
                  Cliente: {pedido.clienteId} | {pedido.lineas} producto(s) | {pedido.moneda}
                </div>
              </div>
              <div style={{ color: '#fff', fontWeight: 700 }}>{formatUSD(pedido.total)}</div>
            </div>
          ))}
        </div>
      )}

      <button onClick={closeSuccessModal} style={{ 
        marginTop: '1.5rem', background: '#00ba52', color: '#000', 
        border: 'none', padding: '0.7rem 2rem', borderRadius: '10px', 
        fontWeight: 700, fontSize: '0.9rem', cursor: 'pointer', 
        fontFamily: 'Montserrat, sans-serif', boxShadow: '0 4px 10px rgba(0,186,82,0.3)' 
      }}>
        Aceptar
      </button>
    </div>
  </>
)}
    </div>
  );
};

export default VistaDetalleProveedor;